/**
 * Authentication service for managing JWT tokens and user authentication
 */
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { resetAllAuthData, setAuthToken, setOrganizationCode } from '../utils/authUtils';
import { API_BASE_URL } from '../utils/constants';
import apiClient from './apiClient';

const API_URL = API_BASE_URL;

/**
 * Store JWT token securely in localStorage
 * @param {string} token - JWT token
 */
export const saveToken = (token) => {
  // Store in jwt_token for our service
  localStorage.setItem('jwt_token', token);
  
  // Also store in authInfo format for AppService compatibility
  try {
    let authInfo = localStorage.getItem('authInfo');
    let authInfoObj = authInfo ? JSON.parse(authInfo) : {};
    authInfoObj.token = token;
    localStorage.setItem('authInfo', JSON.stringify(authInfoObj));
    console.log('Token saved in both formats:', token);
  } catch (error) {
    console.error('Error saving token in authInfo format:', error);
  }
};

/**
 * Store refresh token securely in localStorage
 * @param {string} refreshToken - Refresh token
 */
export const saveRefreshToken = (refreshToken) => {
  // Store in refresh_token for our service
  localStorage.setItem('refresh_token', refreshToken);
  
  // Also store in authInfo format for AppService compatibility
  try {
    let authInfo = localStorage.getItem('authInfo');
    let authInfoObj = authInfo ? JSON.parse(authInfo) : {};
    authInfoObj.refreshToken = refreshToken;
    localStorage.setItem('authInfo', JSON.stringify(authInfoObj));
    console.log('Refresh token saved in both formats:', refreshToken);
  } catch (error) {
    console.error('Error saving refresh token in authInfo format:', error);
  }
};

/**
 * Retrieve JWT token from localStorage
 * @returns {string|null} JWT token or null if not found
 */
export const getToken = () => {
  // First try to get from jwt_token
  const token = localStorage.getItem('jwt_token');
  if (token) return token;
  
  // If not found, try to get from authInfo
  try {
    const authInfo = localStorage.getItem('authInfo');
    if (authInfo) {
      const authInfoObj = JSON.parse(authInfo);
      if (authInfoObj.token) {
        console.log('Token found in authInfo:', authInfoObj.token);
        // Save it in our format for future use
        localStorage.setItem('jwt_token', authInfoObj.token);
        return authInfoObj.token;
      }
    }
  } catch (error) {
    console.error('Error retrieving token from authInfo:', error);
  }
  
  return null;
};

/**
 * Retrieve refresh token from localStorage
 * @returns {string|null} Refresh token or null if not found
 */
export const getRefreshToken = () => {
  // First try to get from refresh_token
  const refreshToken = localStorage.getItem('refresh_token');
  if (refreshToken) return refreshToken;
  
  // If not found, try to get from authInfo
  try {
    const authInfo = localStorage.getItem('authInfo');
    if (authInfo) {
      const authInfoObj = JSON.parse(authInfo);
      if (authInfoObj.refreshToken) {
        console.log('Refresh token found in authInfo:', authInfoObj.refreshToken);
        // Save it in our format for future use
        localStorage.setItem('refresh_token', authInfoObj.refreshToken);
        return authInfoObj.refreshToken;
      }
    }
  } catch (error) {
    console.error('Error retrieving refresh token from authInfo:', error);
  }
  
  return null;
};

/**
 * Clear all authentication tokens from localStorage
 */
export const clearTokens = () => {
  try {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('authInfo');
    localStorage.removeItem('organizationCode'); // Also clear organization code
    console.log('All authentication tokens and organization code cleared');
  } catch (error) {
    console.error('Error clearing tokens from authInfo:', error);
  }
};

/**
 * Parse a JWT token to extract its payload
 * @param {string} token - The JWT token to parse
 * @returns {Object|null} The decoded token payload or null if invalid
 */
export const parseToken = (token) => {
  if (!token) return null;
  
  try {
    // JWT tokens consist of three parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    // Decode the payload (middle part)
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch (error) {
    console.error('Error parsing token:', error);
    return null;
  }
};

/**
 * Check if a JWT token is valid and not expired
 * @param {string} token - The JWT token to validate
 * @returns {boolean} True if the token is valid and not expired
 */
export const isTokenValid = (token) => {
  if (!token) return false;
  
  try {
    // Parse the token
    const payload = parseToken(token);
    if (!payload) return false;
    
    // Check if token has expiration claim
    if (!payload.exp) return false;
    
    // Check if token is expired
    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    
    // Log token expiration information for debugging
    const expirationDate = new Date(expirationTime);
    const currentDate = new Date(currentTime);
    console.log(`Token expiration: ${expirationDate.toLocaleString()}`);
    console.log(`Current time: ${currentDate.toLocaleString()}`);
    console.log(`Token ${currentTime < expirationTime ? 'is valid' : 'has expired'}`);
    
    return currentTime < expirationTime;
  } catch (error) {
    console.error('Error validating token:', error);
    return false;
  }
};

/**
 * Show a popup message about token validation status
 * @param {boolean} isValid - Whether the token is valid
 * @param {string} message - The message to display
 */
export const showTokenStatusPopup = (isValid, message) => {
  // Create popup element if it doesn't exist
  let popup = document.getElementById('token-status-popup');
  if (!popup) {
    popup = document.createElement('div');
    popup.id = 'token-status-popup';
    popup.style.position = 'fixed';
    popup.style.top = '20px';
    popup.style.right = '20px';
    popup.style.padding = '15px 20px';
    popup.style.borderRadius = '5px';
    popup.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
    popup.style.zIndex = '9999';
    popup.style.transition = 'opacity 0.3s ease-in-out';
    popup.style.display = 'flex';
    popup.style.alignItems = 'center';
    popup.style.gap = '10px';
    popup.style.fontFamily = 'Arial, sans-serif';
    popup.style.fontSize = '14px';
    
    document.body.appendChild(popup);
  }
  
  // Set styles based on validation status
  if (isValid) {
    popup.style.backgroundColor = '#4caf50';
    popup.style.color = 'white';
    popup.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg><span>${message}</span>`;
  } else {
    popup.style.backgroundColor = '#f44336';
    popup.style.color = 'white';
    popup.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg><span>${message}</span>`;
  }
  
  // Show popup
  popup.style.opacity = '1';
  
  // Hide popup after 5 seconds
  setTimeout(() => {
    popup.style.opacity = '0';
    setTimeout(() => {
      if (popup.parentNode) {
        popup.parentNode.removeChild(popup);
      }
    }, 300);
  }, 5000);
};

/**
 * Validate the current token and refresh if needed
 * @param {boolean} showPopup - Whether to show a popup with token status
 * @returns {Promise<string|null>} Valid token or null if authentication failed
 */
export const validateToken = async (showPopup = true) => {
  try {
    console.log('Validating authentication token...');
    
    // Get the current token
    const token = getToken();
    
    // Always log the token for debugging (truncated for security)
    if (token) {
      const truncatedToken = token.substring(0, 15) + '...' + token.substring(token.length - 10);
      console.log('Current token (truncated):', truncatedToken);
    } else {
      console.log('No token found');
    }
    
    // If token exists and is valid, return it
    if (token && isTokenValid(token)) {
      console.log('Token is valid');
      if (showPopup) {
        showTokenStatusPopup(true, 'Authentication token is valid');
      }
      return token;
    }
    
    console.log('Token is invalid or expired, attempting to refresh...');
    
    // Try to refresh the token
    const newToken = await refreshToken();
    if (newToken) {
      console.log('Token refreshed successfully');
      if (showPopup) {
        showTokenStatusPopup(true, 'Authentication token refreshed successfully');
      }
      return newToken;
    }
    
    console.error('Failed to refresh token');
    // Clear tokens since they're invalid
    clearTokens();
    
    if (showPopup) {
      showTokenStatusPopup(false, 'Authentication failed. Please log in again.');
    }
    return null;
  } catch (error) {
    console.error('Error validating token:', error);
    // Clear tokens on error
    clearTokens();
    
    if (showPopup) {
      showTokenStatusPopup(false, 'Authentication error. Please log in again.');
    }
    return null;
  }
};

/**
 * Refresh the JWT token using the refresh token
 * @returns {Promise<string|null>} New JWT token or null if refresh failed
 */
export const refreshToken = async () => {
  try {
    const refreshToken = getRefreshToken();
    
    if (!refreshToken) {
      console.error('No refresh token available');
      return null;
    }
    
    // Log refresh token (truncated for security)
    const truncatedRefreshToken = refreshToken.substring(0, 15) + '...' + refreshToken.substring(refreshToken.length - 10);
    console.log('Using refresh token (truncated):', truncatedRefreshToken);
    
    // Try multiple refresh endpoints
    const refreshEndpoints = [
      `${API_URL}/api/auth/refresh`,
      `${API_URL}/v1/auth/refresh`,
      `${API_URL}/auth/refresh`
    ];
    
    let success = false;
    let newToken = null;
    
    for (const endpoint of refreshEndpoints) {
      try {
        console.log(`Trying to refresh token at: ${endpoint}`);
        
        // Create request with proper headers
        const response = await axios.post(endpoint, 
          { refreshToken }, 
          { 
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            } 
          }
        );
        
        console.log('Refresh response:', response.data);
        
        if (response.data && (response.data.token || response.data.accessToken)) {
          // Save the token (handle both possible property names)
          newToken = response.data.token || response.data.accessToken;
          saveToken(newToken);
          
          // Save refresh token if available (handle both possible property names)
          const newRefreshToken = response.data.refreshToken || response.data.refresh_token;
          if (newRefreshToken) {
            saveRefreshToken(newRefreshToken);
          }
          
          console.log('Token refreshed successfully');
          success = true;
          break;
        }
      } catch (endpointError) {
        console.warn(`Failed to refresh token at ${endpoint}:`, endpointError.message);
        if (endpointError.response) {
          console.warn('Response status:', endpointError.response.status);
          console.warn('Response data:', endpointError.response.data);
        }
      }
    }
    
    if (!success) {
      console.error('All refresh token endpoints failed');
      clearTokens();
      return null;
    }
    
    return newToken;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    // If refresh fails, clear tokens to force re-login
    clearTokens();
    return null;
  }
};

/**
 * Authenticate user with username and password
 * @param {string} username - User's username
 * @param {string} password - User's password
 * @returns {Promise<Object>} Authentication result with success status and user info
 */
export const login = async (username, password) => {
  try {
    // Reset ALL authentication data before login to ensure clean state
    resetAllAuthData();
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('organizationCode');
    localStorage.removeItem('currentUser');
    console.log('All previous authentication data cleared before login');
    
    // Try multiple login endpoints
    const loginEndpoints = [
      `${API_URL}/v1/auth/login`,
      `${API_URL}/api/auth/login`,
      `${API_URL}/auth/login`
    ];
    
    let success = false;
    let userData = null;
    
    for (const endpoint of loginEndpoints) {
      try {
        console.log(`Trying to login at: ${endpoint}`);
        const response = await axios.post(endpoint, {
          userName: username,
          password
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        console.log(`Login response from ${endpoint}:`, response.data);
        
        // Check if response contains token
        if (response.data && (response.data.token || response.data.accessToken)) {
          // Save the token (handle both possible property names)
          const token = response.data.token || response.data.accessToken;
          
          // Use the enhanced setAuthToken from authUtils
          setAuthToken(token);
          console.log('Token saved using enhanced setAuthToken');
          
          // Save refresh token if available (handle both possible property names)
          const refreshToken = response.data.refreshToken || response.data.refresh_token;
          if (refreshToken) {
            saveRefreshToken(refreshToken);
          }
          
          // Store user data if available and save organization code
          if (response.data.user) {
            userData = response.data.user;
            
            // Save organization code if available
            if (userData.organisation && userData.organisation.organizationCode) {
              setOrganizationCode(userData.organisation.organizationCode);
              console.log('Organization code saved:', userData.organisation.organizationCode);
            }
            
            // Store user info in localStorage for reference
            localStorage.setItem('currentUser', JSON.stringify(userData));

            // Set Sentry user context for error tracking
            Sentry.setUser({ id: userData.id, username: userData.name, email: userData.email });
            if (userData.roles && userData.roles.length > 0) {
              Sentry.setTag('role', userData.roles[0].code);
            }
            if (userData.organisation && userData.organisation.type) {
              Sentry.setTag('org_type', userData.organisation.type);
            }
          }
          
          // Fetch user details to ensure we have the latest organization info
          try {
            const userResponse = await axios.get(`${API_URL}/api/user/me`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (userResponse.data && userResponse.data.organisation) {
              const orgCode = userResponse.data.organisation.organizationCode;
              if (orgCode) {
                setOrganizationCode(orgCode);
                console.log('Organization code updated from user details:', orgCode);
              }
            }
          } catch (userError) {
            console.warn('Could not fetch additional user details:', userError.message);
          }
          
          // Verify token is valid
          if (token && isTokenValid(token)) {
            console.log('Token validation successful');
            const tokenPayload = parseToken(token);
            console.log('Token expiration:', new Date(tokenPayload.exp * 1000).toLocaleString());
          } else {
            console.warn('Token validation failed, token may be invalid or expired');
          }
          
          console.log('Login successful');
          success = true;
          break;
        }
      } catch (endpointError) {
        console.warn(`Login failed at ${endpoint}:`, endpointError.message);
        if (endpointError.response) {
          console.warn('Response status:', endpointError.response.status);
          console.warn('Response data:', endpointError.response.data);
        }
      }
    }
    
    if (!success) {
      console.error('All login endpoints failed');
      // Make sure to reset auth data on failure
      resetAllAuthData();
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Login failed:', error);
    // Make sure to reset auth data on failure
    resetAllAuthData();
    return false;
  }
};

/**
 * Log out the current user by clearing tokens
 */
export const logout = () => {
  // First clear specific tokens to ensure nothing is missed
  localStorage.removeItem('token');
  localStorage.removeItem('jwt_token');
  localStorage.removeItem('authToken'); 
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('authInfo');
  localStorage.removeItem('organizationCode');
  localStorage.removeItem('currentUser');
  
  // Then use the enhanced reset function to clear all auth data
  resetAllAuthData();
  
  // Clear axios Authorization header
  if (axios.defaults.headers.common['Authorization']) {
    delete axios.defaults.headers.common['Authorization'];
  }
  
  console.log('All authentication tokens and data cleared during logout');
  
  // Redirection is now handled by the component that calls this function
  console.log('Logout complete - redirection will be handled by the calling component');
};

/**
 * Check if the user is currently authenticated
 * @returns {boolean} True if user has a valid token
 */
export const isAuthenticated = () => {
  const token = getToken();
  return !!token; // Convert to boolean
};

/**
 * Get the current user's information from the token
 * @returns {Promise<Object|null>} User information or null if not authenticated
 */
export const getCurrentUser = async () => {
  if (!isAuthenticated()) {
    return null;
  }
  
  try {
    const response = await axios.get(`${API_URL}/api/auth/me`, {
      headers: {
        Authorization: `Bearer ${getToken()}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Failed to get current user:', error);
    return null;
  }
};

/**
 * Get the organization code for the current logged-in user
 * @returns {Promise<string|null>} Organization code or null if not available
 */
export const getOrganizationCode = async () => {
  try {
    // First check if we already have it in localStorage
    const storedOrgCode = localStorage.getItem('organizationCode');
    if (storedOrgCode) {
      console.log('Using stored organization code:', storedOrgCode);
      return storedOrgCode;
    }

    // Get the auth token
    const token = getToken();
    if (!token) {
      console.error('No auth token available');
      return null;
    }

    // Check if we have the organizationId in authInfo
    try {
      let orgId = null;
      
      // Try to get from authInfo in localStorage first
      const authInfo = localStorage.getItem('authInfo');
      if (authInfo) {
        const authInfoObj = JSON.parse(authInfo);
        if (authInfoObj.principalInfo && authInfoObj.principalInfo.organisationId) {
          orgId = authInfoObj.principalInfo.organisationId;
          console.log('Found organization ID in authInfo:', orgId);
        }
      }
      
      // If not found in authInfo, try to extract from token
      if (!orgId) {
        try {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            if (payload && payload.org) {
              orgId = payload.org;
              console.log('Extracted organization ID from token:', orgId);
            }
          }
        } catch (tokenError) {
          console.error('Error parsing token:', tokenError);
        }
      }
      
      // If we have an organization ID, get the details
      if (orgId) {
        try {
          // Direct call to get organization details with code
          console.log(`Fetching organization details for ID: ${orgId}`);
          const orgDetailsResponse = await axios.get(`${API_URL}/v1/organisation/details?organisationId=${orgId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (orgDetailsResponse.data && orgDetailsResponse.data.code) {
            const orgCode = orgDetailsResponse.data.code;
            console.log(`Retrieved organization code from API: ${orgCode}`);
            localStorage.setItem('organizationCode', orgCode);
            return orgCode;
          }
        } catch (detailsError) {
          console.error('Error fetching organization details:', detailsError);
        }
      }

      // Try the user profile API if organization details failed
      const userResponse = await axios.get(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Check if user profile contains organization information
      if (userResponse.data) {
        console.log('User profile data:', userResponse.data);
        
        // If user is a customer based on role
        if (userResponse.data.roles) {
          const hasCustomerRole = userResponse.data.roles.some(role => 
            typeof role.authority === 'string' && 
            role.authority.toLowerCase().includes('customer'));
            
          if (hasCustomerRole) {
            console.log('User has customer role, using code ORG');
            localStorage.setItem('organizationCode', 'ORG');
            return 'ORG';
          }
          
          const hasAdminRole = userResponse.data.roles.some(role => 
            typeof role.authority === 'string' && 
            role.authority.toLowerCase().includes('admin'));
            
          if (hasAdminRole) {
            console.log('User has admin role, using code ORG');
            localStorage.setItem('organizationCode', 'ORG');
            return 'ORG';
          }
        }
      }
    } catch (error) {
      console.error('Error fetching organization information:', error);
    }
    
    // If all else fails, set the default organization code to ORG as per API response
    console.log('Using default organization code: ORG');
    localStorage.setItem('organizationCode', 'ORG');
    return 'ORG';
  } catch (error) {
    console.error('Error getting organization code:', error);
    // Still return ORG as fallback since it's confirmed to work
    localStorage.setItem('organizationCode', 'ORG');
    return 'ORG';
  }
};

export default {
  login,
  logout,
  refreshToken,
  validateToken,
  isAuthenticated,
  getCurrentUser,
  getToken,
  getRefreshToken,
  saveToken,
  clearTokens,
  getOrganizationCode // Added new function
};
